import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Check, Monitor, Play, Smartphone, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useT } from "../i18n/I18nProvider";
import { useDemoCompany, buildDemoState, LOCAL_DEMO_COMPANY } from "./walkthrough2/demoData";
import { RENTER_STEPS } from "./walkthrough2/steps";
import { INCIDENT_DEMO_COMPANY } from "./walkthrough2/incidentScenario";
import DeviceFrame from "./walkthrough2/DeviceFrame";

// The happy path is RENTER_STEPS[0..17]; the "what if something goes wrong"
// branch begins at `issue-notification` - deliberately a phone lock-screen
// notification, not an already-open booking page: Anna hasn't opened
// ConfirmPH yet when this branch starts (see steps.js's own comment). Both
// index into the SAME RENTER_STEPS array by its real absolute position (via
// `indexOffset`) so RenterWalkthroughStage.jsx (rendered inside the iframe,
// driven by postMessage) needs no changes - each mode operates on a LOCAL
// 0-based index and translates to the absolute one when talking to the
// iframe. They stay functionally INDEPENDENT (own indexOffset/stepCount/
// testIdBase); only the outer presentation is unified into one demo section
// with a mode selector.
const ISSUE_BRANCH_START = RENTER_STEPS.findIndex((s) => s.key === "issue-notification");
const HAPPY_PATH_COUNT = ISSUE_BRANCH_START;
const ISSUE_BRANCH_COUNT = RENTER_STEPS.length - ISSUE_BRANCH_START;

// Labels/teasers/CTAs are resolved via t() at render time (labelKey etc.)
// rather than baked in here, so this section reads correctly in both
// English and Filipino: see home.proof.* in i18n/translations.js.
// `defaultDevice`: the issue branch's very first frame must be a phone
// lock-screen notification (see steps.js), which only renders in phone
// mode - so that mode boots straight into phone view instead of desktop.
// The Phone/Desktop toggle stays available afterward either way.
const MODES = [
  {
    key: "normal",
    labelKey: "home.proof.mode.normal",
    indexOffset: 0,
    stepCount: HAPPY_PATH_COUNT,
    testIdBase: "walkthrough",
    teaserKey: "home.proof.teaser.normal",
    ctaKey: "home.proof.cta.normal",
    defaultDevice: "desktop",
  },
  {
    key: "issue",
    labelKey: "home.proof.mode.issue",
    indexOffset: ISSUE_BRANCH_START,
    stepCount: ISSUE_BRANCH_COUNT,
    testIdBase: "issue-walkthrough",
    teaserKey: "home.proof.teaser.issue",
    ctaKey: "home.proof.cta.issue",
    defaultDevice: "phone",
  },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/**
 * A fully self-contained, sandboxed renter walkthrough: real ConfirmPH
 * screens (real presentational components, real styling) driven by local
 * demo state instead of real routing or real API writes. Collapsed by
 * default (a compact "Start preview" teaser, no demo-data fetch) so it
 * doesn't dominate the homepage for visitors who don't want it: the actual
 * interactive player only mounts once someone opts in.
 */
/**
 * A fully self-contained, sandboxed renter walkthrough: real ConfirmPH
 * screens (real presentational components, real styling) driven by local
 * demo state instead of real routing or real API writes. Collapsed by
 * default (a compact "Start preview" teaser, no demo-data fetch) so it
 * doesn't dominate the homepage for visitors who don't want it: the actual
 * interactive player only mounts once someone opts in.
 *
 * ONE demo section, with a Normal/Issue mode selector (`MODES`) switching
 * the content shown inside it, not two separate homepage blocks.
 */
export default function ProductWalkthrough() {
  const { t } = useT();
  const [modeKey, setModeKey] = useState("normal");
  const [started, setStarted] = useState(false);
  const active = MODES.find((m) => m.key === modeKey);

  function selectMode(key) {
    if (key === modeKey) return;
    setModeKey(key);
    setStarted(false);
  }

  return (
    <section
      className="border-t border-slate-200 bg-[#F6F8F7]"
      aria-labelledby="product-walkthrough-title"
      data-testid="product-walkthrough"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-800 font-semibold">{t("home.proof.eyebrow")}</div>
          <h2 id="product-walkthrough-title" className="mt-2 font-display text-3xl sm:text-4xl font-bold text-slate-950">
            {t("home.proof.title")}
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">{t("home.proof.subtitle")}</p>
        </div>

        <ModeSelector modeKey={modeKey} onSelect={selectMode} t={t} />

        {started ? (
          <WalkthroughPlayer key={modeKey} testIdBase={active.testIdBase} indexOffset={active.indexOffset} stepCount={active.stepCount} defaultDeviceMode={active.defaultDevice} />
        ) : (
          <StartPreviewTeaser onStart={() => setStarted(true)} testIdBase={active.testIdBase} teaser={t(active.teaserKey)} cta={t(active.ctaKey)} />
        )}

        <p className="mt-8 max-w-3xl mx-auto text-center text-sm text-slate-600">
          {t("home.proof.business_prompt")}{" "}
          <Link to="/businesses" className="font-semibold text-[#1B4332] hover:underline inline-flex items-center gap-1">
            <Building2 className="w-4 h-4" aria-hidden="true" /> {t("home.proof.business_link")}
          </Link>
        </p>
      </div>
    </section>
  );
}

function ModeSelector({ modeKey, onSelect, t }) {
  return (
    <div className="mt-6 inline-flex flex-wrap items-center gap-1 rounded-full border border-slate-300 bg-white p-1"
         role="tablist" aria-label="Choose a demo" data-testid="walkthrough-mode-selector">
      {MODES.map((m) => (
        <button
          key={m.key}
          type="button"
          role="tab"
          aria-selected={modeKey === m.key}
          onClick={() => onSelect(m.key)}
          data-testid={`walkthrough-mode-${m.key}`}
          className={`min-h-9 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            modeKey === m.key ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          {t(m.labelKey)}
        </button>
      ))}
    </div>
  );
}

function StartPreviewTeaser({ onStart, testIdBase, teaser, cta }) {
  return (
    <div className="mt-6 rounded-3xl border border-slate-200 bg-white shadow-sm p-8 sm:p-12 text-center" data-testid={`${testIdBase}-start-teaser`}>
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center">
        <Play className="w-5 h-5" aria-hidden="true" />
      </div>
      <p className="mt-4 text-sm text-slate-600 max-w-md mx-auto">{teaser}</p>
      <button
        type="button"
        onClick={onStart}
        data-testid={`${testIdBase}-start-preview`}
        className="mt-5 min-h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#1B4332] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#143027] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
      >
        <Play className="w-4 h-4" aria-hidden="true" /> {cta}
      </button>
    </div>
  );
}

/**
 * The actual interactive player: only mounted once the visitor opts in via
 * StartPreviewTeaser, so the demo-data fetch and all walkthrough state stay
 * out of the page's initial load entirely. `localStepIndex` is this widget's
 * own 0-based position; `indexOffset + localStepIndex` is the absolute
 * position in RENTER_STEPS used for content lookup and sent to the iframe.
 */
function WalkthroughPlayer({ testIdBase, indexOffset, stepCount, defaultDeviceMode = "desktop" }) {
  const { company } = useDemoCompany();
  const [localStepIndex, setLocalStepIndex] = useState(0);
  const [deviceMode, setDeviceMode] = useState(defaultDeviceMode);
  // The current internal substep, reported up by RenterWalkthroughStage
  // (inside the iframe) via postMessage: see DeviceFrame's onSubstep and
  // resolveSubstepCopy. null for single-interaction steps, where the outer
  // step's own static description/points already say enough.
  const [substep, setSubstep] = useState(null);
  const reducedMotion = usePrefersReducedMotion();

  const demoState = useMemo(() => {
    const fallback = indexOffset === ISSUE_BRANCH_START ? INCIDENT_DEMO_COMPANY : LOCAL_DEMO_COMPANY;
    return buildDemoState(company || fallback);
  }, [company, indexOffset]);

  const absoluteIndex = indexOffset + localStepIndex;
  const step = RENTER_STEPS[absoluteIndex];
  const Icon = step.icon;

  function goTo(index) {
    setLocalStepIndex(Math.max(0, Math.min(stepCount - 1, index)));
    setSubstep(null); // avoid one frame of the previous step's substep copy
  }

  function onSubstep(stepKey, next) {
    if (stepKey !== step.key) return; // stale message from a step we've left
    setSubstep(next);
  }

  function onEvent(event) {
    if (event.type === "advance") { goTo(localStepIndex + 1); return; }
    if (event.type === "navigate") {
      // One honest, specific shortcut: the real "All rentals" back-link on
      // the business page genuinely should take you back a step here.
      if (event.payload === "/rentals" && (event.stepKey === "business" || event.stepKey === "contact")) {
        goTo(1);
        return;
      }
      toast("That part of the real site isn't part of this walkthrough: follow the highlighted control to continue.", { duration: 2500 });
      return;
    }
    if (event.type === "submit") {
      toast("This is a demo: nothing was actually sent.", { duration: 2000 });
    }
  }

  return (
    <div className="mt-6" data-testid={`${testIdBase}-player`}>
      <div className="rounded-3xl bg-[#E9EFEC] p-3 sm:p-5 lg:p-6" data-testid={`${testIdBase}-active-step`}>
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-12 lg:items-start">
          <div className="rounded-2xl bg-white p-5 sm:p-6 lg:col-span-5" data-testid={`${testIdBase}-step-explainer`}>
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-800">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800">{step.eyebrow}</div>
                <h3 className="mt-1 font-display text-xl font-semibold text-slate-950" data-testid={`${testIdBase}-step-title`}>
                  {substep?.heading || step.title}
                </h3>
              </div>
            </div>
            {substep ? (
              <div data-testid={`${testIdBase}-substep`}>
                <p className="mt-4 text-sm font-medium leading-relaxed text-slate-900">{substep.instruction}</p>
                {substep.context && <p className="mt-2 text-sm leading-relaxed text-slate-600">{substep.context}</p>}
              </div>
            ) : (
              <>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{step.description}</p>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {(step.points || []).map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="min-w-0 lg:col-span-7">
            <div className="mb-3 flex justify-center sm:justify-end">
              <PreviewDeviceToggle mode={deviceMode} setMode={setDeviceMode} testIdBase={testIdBase} />
            </div>
            <DeviceFrame mode={deviceMode} stepIndex={absoluteIndex} demoState={demoState} reducedMotion={reducedMotion} onEvent={onEvent} onSubstep={onSubstep} resetToken={0} testIdBase={testIdBase} />
          </div>
        </div>

        {/* Manual fallback: real interactions inside the frame are meant to
            advance the story on their own; this is only here so a visitor who
            gets stuck (a missed click, a real page with no obvious next step)
            always has a way to move. */}
        <div className="mt-4 flex items-center justify-center gap-3 text-sm text-slate-500" data-testid={`${testIdBase}-manual-nav`}>
          <button
            type="button"
            onClick={() => goTo(localStepIndex - 1)}
            disabled={localStepIndex === 0}
            data-testid={`${testIdBase}-prev`}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Previous
          </button>
          <span className="text-xs text-slate-400">{localStepIndex + 1} / {stepCount} · stuck? use these</span>
          <button
            type="button"
            onClick={() => goTo(localStepIndex + 1)}
            disabled={localStepIndex === stepCount - 1}
            data-testid={`${testIdBase}-next`}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewDeviceToggle({ mode, setMode, testIdBase }) {
  return (
    <div className="inline-flex items-center rounded-full border border-slate-300 bg-white p-0.5"
         data-testid={`${testIdBase}-device-toggle`} role="group" aria-label="Preview device">
      <button type="button" onClick={() => setMode("desktop")}
              data-testid={`${testIdBase}-device-desktop`}
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${mode === "desktop" ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Show the preview as a desktop browser">
        <Monitor className="w-3.5 h-3.5" /> Desktop
      </button>
      <button type="button" onClick={() => setMode("phone")}
              data-testid={`${testIdBase}-device-phone`}
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${mode === "phone" ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Show the preview as a real phone viewport">
        <Smartphone className="w-3.5 h-3.5" /> Phone
      </button>
    </div>
  );
}
